<template>
  <div>
    <v-container>
      <v-row>
        <v-col cols="3">
          <k-card-header title="ATM Settings" :items="headerItems"></k-card-header>
        </v-col>
          <v-col  v-if="allAtms"  cols="5" class="ml">
            <v-select  :loading="loading" v-model="atm" label="Atms"
                       ref="atmSelect" outlined dense hide-details return-object :items="allAtms">
              <template slot="selection" slot-scope="data">
                <!-- HTML that describe how select should render selected items -->
                {{ getDescription(data.item) }}
              </template>
              <template slot="item" slot-scope="data">
                <!-- HTML that describe how select should render items when the select is open -->
                {{ getDescription(data.item) }}
              </template>
            </v-select>
          </v-col>

          <!-- Edit button -->
          <v-col cols="auto" class="ml">
            <v-btn
                color="primary"
                ref="editButtonAtm"
                class="pl-8 pr-8"
                @click="editAtmSettings()"
                :disabled="createAtmSettingsPressed || saving || this.atm === null"
            >Edit
            </v-btn>
          </v-col>
          <!-- Create button -->
          <v-col cols="auto" class="mr-auto">
            <v-btn
                color="primary"
                ref="createButtonAtm"
                class="pl-8 pr-8"
                @click="createAtmSettings()"
                :disabled="createAtmSettingsPressed || saving"
            >Create
            </v-btn>
          </v-col>
      </v-row>
    </v-container>

    <v-container v-if="loading">
      <v-progress-circular indeterminate size="50" />
    </v-container>

    <v-container v-if="!loading && workingATM">
      <v-card class="detailCard">
        <v-card-text>
                <v-container>
                  <v-row justify="start" class="ma-0 pa-0">
                    <v-col cols="12" md="2" class="ma-0 pa-0">
                      <b>Design Start Date</b>
                    </v-col>
                    <v-col cols="12" md="6" class="ma-0 pa-0">
                      <text-box-date-picker v-model="workingATM.designStartDate"
                                            ref="designStartDate" :disabled="!isEditable"/>
                    </v-col>
                  </v-row>
                  <!-- Terminal Fee Calculation Method -->
                  <v-row justify="start" class="ma-0 pa-0">
                    <v-col cols="12" md="2" class="ma-0 pa-0">
                        <b>Terminal Fee Calculation Method</b>
                    </v-col>
                    <v-col cols="12" md="6" class="ma-0 pa-0">
                      <v-radio-group v-model="workingATM.terminalFeeCalculationMethod"
                        ref="terminalFeeCalculationMethod"
                        class="ma-0 pa-0" :disabled="!isEditable">
                        <v-radio
                          label="Terminal Fee is provided in a separate field"
                          value="CALCULATE_FROM_TERMINAL_FEE_FIELD"
                          ref="calculate_from_terminal_fee_field"
                          class="ma-0 pa-0"
                          @click="setTerminalFeeModulusToNull()"
                        />
                        <v-radio
                          label="Calculate Terminal Fee from transaction amount"
                          value="CALCULATE_FROM_TRANSACTION_AMOUNT"
                          ref="calculate_from_transaction_amount"
                          class="ma-0 pa-0"
                          @click="setTerminalFeeModulusToExistingValue()"
                        />
                        <v-radio
                          label="Terminal Fee is separate transaction"
                          value="CALCULATE_FROM_TERMINAL_FEE_TRANSACTION"
                          ref="calculate_from_terminal_fee_transaction"
                          class="ma-0 pa-0"
                          @click="setTerminalFeeModulusToNull()"
                        />
                      </v-radio-group>
                    </v-col>
                  </v-row>

                  <!-- Terminal Fee Modulus -->
                  <v-row justify="start" class="ma-0 pa-0"
                    v-if="workingATM.terminalFeeCalculationMethod === 'CALCULATE_FROM_TRANSACTION_AMOUNT'">
                    <v-col cols="12" md="2" class="ma-0 pa-0">
                      <b>Terminal Fee Modulus</b>
                    </v-col>
                    <v-col cols="12" md="6" class="ma-0 pa-0">
                      <v-radio-group v-model="workingATM.terminalFeeModulus"
                        ref="terminalFeeModulus"
                        class="ma-0 pa-0" :disabled="!isEditable">
                        <v-radio
                          label="$5.00"
                          value="FIVE_DOLLARS"
                          ref="five_dollars"
                          class="ma-0 pa-0"
                        />
                        <v-radio
                          label="$10.00"
                          value="TEN_DOLLARS"
                          ref="ten_dollars"
                          class="ma-0 pa-0"
                        />
                      </v-radio-group>
                    </v-col>
                  </v-row>

                  <!-- ATM Transaction Filter -->
                  <v-row justify="start" class="ma-0 pa-0">
                    <v-col cols="12" md="2" class="ma-0 pa-0">
                      <b>ATM Transaction Filter</b>
                    </v-col>
                    <v-col cols="12" md="6" class="ma-0 pa-0">
                      <v-radio-group v-model="workingATM.atmTransactionFilter"
                        ref="atmTransactionFilter"
                        class="ma-0 pa-0" :disabled="!isEditable">
                        <v-radio
                          label="All Values"
                          value="ALL_VALUES"
                          ref="all_values"
                          class="ma-0 pa-0"
                        />
                        <v-radio
                          label="Only multiples of a nickel (.05), .01, .49, .98, and .99"
                          value="FILTERED_VALUES"
                          ref="filtered_values"
                          class="ma-0 pa-0"
                        />
                      </v-radio-group>
                    </v-col>
                  </v-row>

                  <!-- ATM Refund Aggregation -->
                  <v-row justify="start" class="ma-0 pa-0">
                    <v-col cols="12" md="2" class="ma-0 pa-0">
                      <b>ATM Refund Aggregation</b>
                    </v-col>
                    <v-col cols="12" md="6" class="ma-0 pa-0">
                      <v-radio-group v-model="workingATM.atmRefundAggregatorSetting"
                        ref="atmRefundAggregatorSetting"
                        class="ma-0 pa-0" :disabled="!isEditable">
                        <v-radio
                          label="Combine all ATM Refunds"
                          value="COMBINE_ALL"
                          ref="combine_all"
                          class="ma-0 pa-0"
                        />
                        <v-radio
                          label="Separate ATM Fee Refunds and Internal ATM Surcharge Refunds"
                          value="SEPARATE"
                          ref="separate"
                          class="ma-0 pa-0"
                        />
                      </v-radio-group>
                    </v-col>
                  </v-row>

                </v-container>
              </v-card-text>
            </v-card>

        <v-row class="pa-0 ma-0">
          <v-spacer></v-spacer>
          <!-- Reset button -->
          <v-col cols="auto" class="mr-auto">
              <v-btn
                color="primary"
                ref="resetButton"
                @click="initializeWorkingATM()"
                :loading="cancelling"
                :disabled="!isDataModified || saving"
                >Reset
              </v-btn>
          </v-col>

          <!-- Save button -->
          <v-col cols="auto" class="mr-auto">
            <v-btn
              color="primary"
              ref="saveButton"
              @click="saveATMSettings()"
              :loading="saving"
              :disabled="!isDataModified"
              >Save
            </v-btn>
          </v-col>
        </v-row>
    </v-container>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import {
  Component, Inject, Watch,
} from 'vue-property-decorator';
import { mapState } from 'vuex';
import { Alert, NoticeClass } from '@kasasa/fbase-components/lib';
import { KCardHeader } from '@kasasa/fbase-components';
import { TextBoxDatePicker } from '@kasasa/portal-components';
import { DateTool } from '@/util';
import {
  ATM,
  ATMSettingsResource,
} from '@/api/atmsettings';

@Component({
  computed: mapState(['fiId']),
  components: {
    KCardHeader,
    'text-box-date-picker': TextBoxDatePicker,
  },
})
export default class ATMSettings extends Vue {
  headerItems: any = [
    {
      text: 'Rewards Configuration',
      disabled: false,
      link: false,
    },
    {
      text: 'ATM Settings',
      disabled: true,
    },
  ];

  @Inject('ATMSettingsResource')
  atmSettingsResource!: ATMSettingsResource;

  fiId!: number;

  allAtms: | ATM[] = [];

  currentDesignIndex: number = 0;

  atm: ATM | null = null;

  workingATM: ATM = ATMSettingsResource.createEmptyATM(null);

  loading: boolean = false;

  createAtmSettingsPressed: boolean = false;

  cancelling: boolean = false;

  saving: boolean = false;

  isDataModified: boolean = false;

  isEditable: boolean = false;

  valid: boolean = false;

  @Watch('fiId')
  mounted() {
    this.atm = null;
    this.allAtms = [];
    this.loading = true;
    this.atmSettingsResource
      .getMany(this.fiId)
      .then((res: ATM[]) => {
        this.allAtms = res;
        if (this.allAtms.length > 0) {
          this.atm = this.allAtms[0];
        } else {
          this.workingATM = ATMSettingsResource.createEmptyATM(null);
        }
        this.currentDesignIndex = this.getCurrentDesignIndex();
      })
      .catch((error) => {
        this.showError(error);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  loadAtms() {
    this.allAtms = [];
    this.loading = true;
    this.atmSettingsResource
      .getMany(this.fiId)
      .then((res: ATM[]) => {
        this.allAtms = res;
        this.currentDesignIndex = this.getCurrentDesignIndex();
      })
      .catch((error) => {
        this.showError(error);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  @Watch('atm')
  initializeWorkingATM() {
    this.isDataModified = false;
    this.isEditable = false;
    this.createAtmSettingsPressed = false;
    if (this.atm) {
      Object.assign(this.workingATM, this.atm);
    } else {
      this.workingATM = ATMSettingsResource.createEmptyATM(null);
    }
  }

  getDescription(atm:ATM) : string {
    let startString : string = '';
    const atmIndex : number = this.getAtmIndex(atm);
    if (atmIndex === this.currentDesignIndex) {
      startString = 'Current ';
    }

    const startDateString = DateTool.toFriendlyFormatFromIsoFormatString(atm.designStartDate);
    if (this.allAtms.length === 1 || atmIndex === 0) {
      return `${startString}Settings Design - ${startDateString}`;
    }

    return `${startString}Settings Design - ${startDateString} - ${DateTool.subtractOneDay(this.allAtms[atmIndex - 1].designStartDate)}`;
  }

  getCurrentDesignIndex() : number {
    for (let i = 0; i < this.allAtms.length; i++) {
      if (DateTool.parseDate(this.allAtms[i].designStartDate) < new Date()) {
        return i;
      }
    }
    return -1;
  }

  getAtmIndex(atm: ATM) : number {
    const isEqual = (element : ATM) => element === atm;
    return this.allAtms.findIndex(isEqual);
  }

  editAtmSettings() {
    this.isEditable = true;
  }

  createAtmSettings() {
    this.createAtmSettingsPressed = true;
    this.isDataModified = false;
    this.isEditable = true;
    if (this.atm) {
      this.workingATM.designStartDate = DateTool.todayBackendDate();
      this.workingATM.designVersionId = null;
    } else {
      this.workingATM = ATMSettingsResource.createEmptyATM(null);
    }
  }

  @Watch('workingATM', { deep: true })
  onModification() {
    if (this.atm === null) {
      this.isDataModified = true;
      return;
    }
    if (this.workingATM.terminalFeeCalculationMethod !== this.atm!.terminalFeeCalculationMethod
          || this.workingATM.terminalFeeModulus !== this.atm!.terminalFeeModulus
          || this.workingATM.atmTransactionFilter !== this.atm!.atmTransactionFilter
          || this.workingATM.atmRefundAggregatorSetting !== this.atm!.atmRefundAggregatorSetting
          || this.workingATM.designStartDate !== this.atm!.designStartDate) {
      this.isDataModified = true;
    } else {
      this.isDataModified = false;
    }
  }

  isValid() {
    if (this.workingATM.terminalFeeCalculationMethod === '') {
      this.setError('Terminal Fee Calculation Method is required!');
      return false;
    }
    if (this.workingATM.terminalFeeCalculationMethod === 'CALCULATE_FROM_TRANSACTION_AMOUNT'
        && (this.workingATM.terminalFeeModulus === '' || this.workingATM.terminalFeeModulus === null)) {
      this.setError('Terminal Fee Modulus is required!');
      return false;
    }
    if (this.workingATM.atmTransactionFilter === '') {
      this.setError('ATM Transaction Filter is required!');
      return false;
    }
    if (this.workingATM.atmRefundAggregatorSetting === '') {
      this.setError('ATM Refund Aggregation is required!');
      return false;
    }
    const isDuplicateDate = (element : ATM) => element.designStartDate === this.workingATM.designStartDate
        && element.designVersionId !== this.workingATM.designVersionId;
    const duplicateDateIndex : number = this.allAtms.findIndex(isDuplicateDate);
    if (duplicateDateIndex > -1) {
      this.setError('Two Atm Settings cannot share the same start date!');
      return false;
    }
    return true;
  }

  saveATMSettings() {
    if (!this.isValid()) {
      return;
    }

    this.saving = true;
    this.atmSettingsResource
      .update(this.fiId, this.workingATM!)
      .then((result: any) => {
        this.atm = result;
        const al = new Alert('ATM Settings saved successfully.', NoticeClass.SUCCESS);
        al.setTimeout(4000);
        this.$store.dispatch('notices/add', al);
      })
      .finally(() => {
        this.saving = false;
        this.createAtmSettingsPressed = false;
        this.isEditable = false;
        this.loadAtms();
      })
      .catch((error) => {
        this.showError(error);
      });
  }

  setTerminalFeeModulusToNull() {
    this.workingATM.terminalFeeModulus = null;
  }

  setTerminalFeeModulusToExistingValue() {
    this.workingATM.terminalFeeModulus = this.atm!.terminalFeeModulus;
  }

  showError(error: any) {
    if (error.response && error.response.data && error.response.data.entity) {
      this.setError(error.response.data.entity);
    } else {
      this.setError('An unexpected error occurred.');
    }
  }

  setError(message: string) {
    const al = new Alert(message, NoticeClass.ERROR);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);
  }
}
</script>

<style lang="scss">
</style>
